import React, { useRef } from 'react';
import styled from 'styled-components';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';

const SpotlightSection = styled.section`
  padding: var(--spacing-xl) 0;
  background-color: var(--color-bg-dark);
  position: relative;
  overflow: hidden;
`;

const GradientBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at 70% 30%, var(--color-accent-primary) 0%, transparent 70%);
  opacity: 0.1;
  z-index: 0;
  pointer-events: none;
`;

const ContentContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 var(--spacing-md);
  position: relative;
  z-index: 1;
`;

const SpotlightContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-lg);
  align-items: center;
  position: relative;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: var(--spacing-md);
  }
`;

const SpotlightTextContent = styled.div`
  position: relative;
  
  @media (max-width: 768px) {
    order: 2;
  }
`;

const SpotlightTag = styled(motion.div)`
  display: inline-block;
  padding: 0.5rem 1rem;
  background-color: rgba(166, 140, 255, 0.1);
  color: var(--color-accent-primary);
  border-radius: var(--radius-full);
  margin-bottom: var(--spacing-md);
  font-size: 0.875rem;
  font-weight: 500;
  letter-spacing: 1px;
  text-transform: uppercase;
  border: 1px solid rgba(166, 140, 255, 0.3);
`;

const SpotlightTitle = styled(motion.h2)`
  margin-bottom: var(--spacing-md);
  
  span {
    color: var(--color-accent-primary);
  }
`;

const SpotlightDescription = styled(motion.p)`
  margin-bottom: var(--spacing-md);
  font-size: 1.125rem;
  line-height: 1.8;
  color: var(--color-text-secondary);
`;

const FeatureList = styled.ul`
  list-style: none;
  margin-top: var(--spacing-md);
`;

const FeatureItem = styled(motion.li)`
  display: flex;
  align-items: flex-start;
  margin-bottom: var(--spacing-sm);
  
  svg {
    margin-right: 1rem;
    flex-shrink: 0;
    color: var(--color-accent-primary);
  }
`;

const FeatureText = styled.div`
  h4 {
    margin-bottom: 0.25rem;
    font-size: 1.25rem;
  }
  
  p {
    color: var(--color-text-secondary);
    font-size: 1rem;
    margin-bottom: 0;
  }
`;

const SpotlightImageContainer = styled(motion.div)`
  position: relative;
  
  @media (max-width: 768px) {
    order: 1;
    margin-bottom: var(--spacing-md);
  }
`;

const SpotlightImage = styled(motion.div)`
  position: relative;
  width: 100%;
  height: 0;
  padding-bottom: 100%;
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  
  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(166, 140, 255, 0.2) 0%, rgba(0, 0, 0, 0) 100%);
  }
`;

const ImageFrame = styled(motion.div)`
  position: absolute;
  top: -10px;
  left: -10px;
  right: -10px;
  bottom: -10px;
  border: 2px solid var(--color-accent-primary);
  border-radius: var(--radius-lg);
  opacity: 0.3;
  z-index: -1;
`;

const ImageDecoration = styled(motion.div)`
  position: absolute;
  bottom: -30px;
  right: -30px;
  width: 80px;
  height: 80px;
  border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%;
  background-color: var(--color-accent-primary);
  opacity: 0.2;
  z-index: -1;
`;

const ImageDecorationDot = styled(motion.div)`
  position: absolute;
  bottom: 50px;
  left: -20px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: var(--color-accent-secondary);
  opacity: 0.2;
  z-index: -1;
`;

const SpotlightButton = styled(motion.button)`
  background-color: var(--color-accent-primary);
  color: var(--color-bg-dark);
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: var(--radius-full);
  font-weight: 500;
  font-size: 1rem;
  letter-spacing: 1px;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: var(--spacing-md);
  box-shadow: 0 4px 15px rgba(166, 140, 255, 0.25);
  transition: all var(--transition-fast);
  cursor: pointer;
  
  &:hover {
    background-color: var(--color-accent-secondary);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(166, 140, 255, 0.4);
  }
`;

const FeatureSpotlight = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.2 });
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  
  const imageY = useTransform(scrollYProgress, [0, 1], [30, -30]);
  const frameScale = useTransform(scrollYProgress, [0, 1], [0.95, 1.05]);
  
  const featureItems = [
    {
      title: "Specialized Assessment",
      description: "Tailored evaluations for identifying autism spectrum characteristics"
    },
    {
      title: "Inclusive Strategies",
      description: "Evidence-based approaches for classroom integration and peer interaction"
    },
    {
      title: "Family Empowerment",
      description: "Tools for parents to support development and advocate effectively"
    }
  ];
  
  return (
    <SpotlightSection ref={ref} id="approach">
      <GradientBackground />
      
      <ContentContainer>
        <SpotlightContent>
          <SpotlightTextContent>
            <SpotlightTag
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.5 }}
            >
              Featured Expertise
            </SpotlightTag>
            
            <SpotlightTitle
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Supporting Students with <span>Autism</span>
            </SpotlightTitle>
            
            <SpotlightDescription
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              My specialized experience with autism center-based programs allows me to create environments where students with autism can develop social-emotional skills, executive functioning capabilities, and self-advocacy. I'm dedicated to fostering inclusive educational spaces that recognize and celebrate neurodiversity.
            </SpotlightDescription>
            
            <SpotlightDescription
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              I work collaboratively with educators and families to implement strategies that support the unique learning profiles of autistic students, ensuring they have access to meaningful educational experiences tailored to their individual strengths and needs.
            </SpotlightDescription>
            
            <FeatureList>
              {featureItems.map((item, index) => (
                <FeatureItem
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  
                  <FeatureText>
                    <h4>{item.title}</h4>
                    <p>{item.description}</p>
                  </FeatureText>
                </FeatureItem>
              ))}
            </FeatureList>
            
            <SpotlightButton
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
            >
              Explore All Services
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </SpotlightButton>
          </SpotlightTextContent>
          
          <SpotlightImageContainer
            initial={{ opacity: 0, x: 20 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 20 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <SpotlightImage style={{ y: imageY }}>
              <img src="/images/laura-profile.jpeg" alt="Laura St. Pierre working with students" />
            </SpotlightImage>
            
            <ImageFrame
              style={{ scale: frameScale }}
              animate={{ 
                opacity: [0.2, 0.4, 0.2],
                transition: { 
                  repeat: Infinity, 
                  duration: 10,
                  ease: "easeInOut" 
                }
              }}
            />
            
            <ImageDecoration
              animate={{ 
                scale: [1, 1.1, 1, 0.95, 1],
                transition: { 
                  repeat: Infinity, 
                  duration: 20,
                  ease: "easeInOut" 
                }
              }}
            />
            
            <ImageDecorationDot
              animate={{ 
                y: [0, 10, 0, -10, 0],
                transition: { 
                  repeat: Infinity, 
                  duration: 15,
                  ease: "easeInOut" 
                }
              }}
            />
          </SpotlightImageContainer>
        </SpotlightContent>
      </ContentContainer>
    </SpotlightSection>
  );
};

export default FeatureSpotlight;